/**
 * FitnessProfileWizard Component
 *
 * Multi-step wizard for collecting client fitness profile information.
 * Steps: Training Background -> Goals -> Equipment -> Limitations
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  User,
  Target,
  Dumbbell,
  AlertTriangle,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import EquipmentSurvey from './EquipmentSurvey';
import {
  useClientFitnessProfile,
  TrainingExperience,
  PrimaryGoal,
  TrainingLocation,
  Injury,
  FitnessProfileInput,
} from '@/hooks/use-client-fitness-profile';

// Step configuration
const STEPS = [
  {
    id: 'background',
    title: 'Training Background',
    description: 'Tell us about your training experience',
    icon: User,
  },
  {
    id: 'goals',
    title: 'Goals',
    description: 'What do you want to achieve?',
    icon: Target,
  },
  {
    id: 'equipment',
    title: 'Equipment',
    description: 'What equipment do you have access to?',
    icon: Dumbbell,
  },
  {
    id: 'limitations',
    title: 'Limitations',
    description: 'Any injuries or restrictions?',
    icon: AlertTriangle,
  },
] as const;

// Options for dropdowns
const EXPERIENCE_OPTIONS: { value: TrainingExperience; label: string; description: string }[] = [
  { value: 'beginner', label: 'Beginner', description: 'Less than 1 year of consistent training' },
  {
    value: 'intermediate',
    label: 'Intermediate',
    description: '1-3 years of consistent training',
  },
  { value: 'advanced', label: 'Advanced', description: '3-5+ years of serious training' },
  {
    value: 'elite',
    label: 'Elite',
    description: 'Competitive athlete or 5+ years training',
  },
];

const GOAL_OPTIONS: { value: PrimaryGoal; label: string; description: string }[] = [
  { value: 'strength', label: 'Build Strength', description: 'Increase maximal force production' },
  {
    value: 'hypertrophy',
    label: 'Build Muscle',
    description: 'Maximize muscle growth and size',
  },
  { value: 'fat_loss', label: 'Lose Fat', description: 'Reduce body fat while preserving muscle' },
  { value: 'endurance', label: 'Improve Endurance', description: 'Enhance cardiovascular fitness' },
  {
    value: 'athletic_performance',
    label: 'Athletic Performance',
    description: 'Sport-specific training',
  },
  {
    value: 'general_fitness',
    label: 'General Fitness',
    description: 'Overall health and wellness',
  },
  {
    value: 'rehabilitation',
    label: 'Rehabilitation',
    description: 'Recovery from injury or surgery',
  },
];

const LOCATION_OPTIONS: { value: TrainingLocation; label: string; description: string }[] = [
  { value: 'commercial_gym', label: 'Commercial Gym', description: 'Full gym with all equipment' },
  { value: 'home_gym', label: 'Home Gym', description: 'Personal setup at home' },
  { value: 'minimal', label: 'Minimal Equipment', description: 'Basic equipment only' },
  { value: 'outdoor', label: 'Outdoor/Calisthenics', description: 'Parks, bodyweight focus' },
];

const INJURY_AREAS = [
  'Shoulder',
  'Elbow',
  'Wrist',
  'Lower Back',
  'Upper Back',
  'Hip',
  'Knee',
  'Ankle',
  'Neck',
  'Other',
];

interface FitnessProfileWizardProps {
  clientId: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

export default function FitnessProfileWizard({
  clientId,
  onComplete,
  onCancel,
}: FitnessProfileWizardProps) {
  const { profile, upsertProfile, isLoading } = useClientFitnessProfile({ clientId });

  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FitnessProfileInput>({
    training_experience: profile?.training_experience || null,
    training_years: profile?.training_years || null,
    current_training_frequency: profile?.current_training_frequency || null,
    preferred_session_duration: profile?.preferred_session_duration || null,
    primary_goal: profile?.primary_goal || null,
    secondary_goals: profile?.secondary_goals || [],
    goal_notes: profile?.goal_notes || null,
    available_equipment: profile?.available_equipment || [],
    training_location: profile?.training_location || null,
    injuries: profile?.injuries || [],
    movement_restrictions: profile?.movement_restrictions || [],
  });

  // Injury form state
  const [showInjuryForm, setShowInjuryForm] = useState(false);
  const [newInjury, setNewInjury] = useState<Partial<Injury>>({
    area: '',
    severity: 'minor',
    notes: '',
    is_active: true,
  });

  const updateFormData = (updates: Partial<FitnessProfileInput>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleAddInjury = () => {
    if (!newInjury.area) return;

    const injury: Injury = {
      area: newInjury.area || '',
      severity: newInjury.severity as 'minor' | 'moderate' | 'severe',
      notes: newInjury.notes || '',
      is_active: newInjury.is_active ?? true,
    };

    updateFormData({
      injuries: [...(formData.injuries || []), injury],
    });

    setNewInjury({ area: '', severity: 'minor', notes: '', is_active: true });
    setShowInjuryForm(false);
  };

  const handleRemoveInjury = (index: number) => {
    updateFormData({
      injuries: (formData.injuries || []).filter((_, i) => i !== index),
    });
  };

  const handleComplete = async () => {
    try {
      await upsertProfile.mutateAsync(formData);
      onComplete?.();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const currentStepConfig = STEPS[currentStep];
  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const isLastStep = currentStep === STEPS.length - 1;

  // Validation per step
  const isStepValid = () => {
    switch (currentStep) {
      case 0: // Background
        return formData.training_experience && formData.training_location;
      case 1: // Goals
        return formData.primary_goal;
      case 2: // Equipment
        return (formData.available_equipment?.length || 0) > 0;
      case 3: // Limitations
        return true; // Optional step
      default:
        return true;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{currentStepConfig.title}</h2>
            <p className="text-sm text-muted-foreground">{currentStepConfig.description}</p>
          </div>
          <Badge variant="secondary">
            Step {currentStep + 1} of {STEPS.length}
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isComplete = index < currentStep;

            return (
              <div
                key={step.id}
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                  isActive && 'border-primary bg-primary text-primary-foreground',
                  isComplete && 'border-primary bg-primary/20 text-primary',
                  !isActive && !isComplete && 'border-muted bg-muted/50 text-muted-foreground'
                )}
              >
                {isComplete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Training Background */}
          {currentStep === 0 && (
            <div className="space-y-6">
              {/* Experience Level */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Training Experience *</Label>
                <RadioGroup
                  value={formData.training_experience || ''}
                  onValueChange={(value) =>
                    updateFormData({ training_experience: value as TrainingExperience })
                  }
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                  {EXPERIENCE_OPTIONS.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={option.value}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all',
                          'hover:bg-muted/50',
                          'peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5'
                        )}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Training Location */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Primary Training Location *</Label>
                <RadioGroup
                  value={formData.training_location || ''}
                  onValueChange={(value) =>
                    updateFormData({ training_location: value as TrainingLocation })
                  }
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                  {LOCATION_OPTIONS.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={`loc-${option.value}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`loc-${option.value}`}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all',
                          'hover:bg-muted/50',
                          'peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5'
                        )}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="training_years">Years Training</Label>
                  <Input
                    id="training_years"
                    type="number"
                    min={0}
                    max={50}
                    placeholder="e.g., 2"
                    value={formData.training_years || ''}
                    onChange={(e) =>
                      updateFormData({
                        training_years: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="frequency">Days per Week</Label>
                  <Select
                    value={formData.current_training_frequency?.toString() || ''}
                    onValueChange={(value) =>
                      updateFormData({ current_training_frequency: parseInt(value) })
                    }
                  >
                    <SelectTrigger id="frequency">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <SelectItem key={n} value={n.toString()}>
                          {n} day{n > 1 ? 's' : ''}/week
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Session Duration</Label>
                  <Select
                    value={formData.preferred_session_duration?.toString() || ''}
                    onValueChange={(value) =>
                      updateFormData({ preferred_session_duration: parseInt(value) })
                    }
                  >
                    <SelectTrigger id="duration">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {[30, 45, 60, 75, 90, 120].map((n) => (
                        <SelectItem key={n} value={n.toString()}>
                          {n} minutes
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Goals */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Primary Goal */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Primary Goal *</Label>
                <RadioGroup
                  value={formData.primary_goal || ''}
                  onValueChange={(value) =>
                    updateFormData({ primary_goal: value as PrimaryGoal })
                  }
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                  {GOAL_OPTIONS.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={`goal-${option.value}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`goal-${option.value}`}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all',
                          'hover:bg-muted/50',
                          'peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5'
                        )}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Secondary Goals */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Secondary Goals (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Select any additional goals you want to work towards
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {GOAL_OPTIONS.filter((g) => g.value !== formData.primary_goal).map((option) => {
                    const isSelected = formData.secondary_goals?.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className={cn(
                          'flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors',
                          isSelected
                            ? 'bg-primary/10 border-primary/50'
                            : 'hover:bg-muted/50'
                        )}
                        onClick={() => {
                          const current = formData.secondary_goals || [];
                          updateFormData({
                            secondary_goals: isSelected
                              ? current.filter((g) => g !== option.value)
                              : [...current, option.value],
                          });
                        }}
                      >
                        <Checkbox checked={isSelected} />
                        <span className="text-sm">{option.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Goal Notes */}
              <div className="space-y-2">
                <Label htmlFor="goal_notes">Additional Notes</Label>
                <Textarea
                  id="goal_notes"
                  placeholder="Any specific goals, events, or timelines you're working towards..."
                  value={formData.goal_notes || ''}
                  onChange={(e) => updateFormData({ goal_notes: e.target.value || null })}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 3: Equipment */}
          {currentStep === 2 && (
            <EquipmentSurvey
              value={formData.available_equipment || []}
              onChange={(equipment) => updateFormData({ available_equipment: equipment })}
            />
          )}

          {/* Step 4: Limitations */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Injuries & Limitations</Label>
                    <p className="text-xs text-muted-foreground">
                      This helps us avoid exercises that could aggravate existing issues
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowInjuryForm(true)}
                    disabled={showInjuryForm}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Injury
                  </Button>
                </div>

                {/* Current Injuries List */}
                {(formData.injuries?.length || 0) > 0 && (
                  <div className="space-y-2">
                    {formData.injuries?.map((injury, index) => (
                      <div
                        key={index}
                        className={cn(
                          'flex items-start justify-between p-4 rounded-lg border',
                          injury.is_active ? 'bg-destructive/10 border-destructive/30' : 'bg-muted/50'
                        )}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <AlertTriangle
                              className={cn(
                                'h-4 w-4',
                                injury.is_active ? 'text-destructive' : 'text-muted-foreground'
                              )}
                            />
                            <span className="font-medium">{injury.area}</span>
                            <Badge
                              variant={
                                injury.severity === 'severe'
                                  ? 'destructive'
                                  : injury.severity === 'moderate'
                                    ? 'default'
                                    : 'secondary'
                              }
                            >
                              {injury.severity}
                            </Badge>
                            {!injury.is_active && (
                              <Badge variant="outline">Healed</Badge>
                            )}
                          </div>
                          {injury.notes && (
                            <p className="text-sm text-muted-foreground">{injury.notes}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveInjury(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Injury Form */}
                {showInjuryForm && (
                  <Card className="border-dashed">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Add Injury/Limitation</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Area *</Label>
                          <Select
                            value={newInjury.area}
                            onValueChange={(value) =>
                              setNewInjury((prev) => ({ ...prev, area: value }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select area" />
                            </SelectTrigger>
                            <SelectContent>
                              {INJURY_AREAS.map((area) => (
                                <SelectItem key={area} value={area}>
                                  {area}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Severity</Label>
                          <Select
                            value={newInjury.severity}
                            onValueChange={(value) =>
                              setNewInjury((prev) => ({
                                ...prev,
                                severity: value as 'minor' | 'moderate' | 'severe',
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="minor">Minor</SelectItem>
                              <SelectItem value="moderate">Moderate</SelectItem>
                              <SelectItem value="severe">Severe</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea
                          placeholder="Describe the injury and what movements to avoid..."
                          value={newInjury.notes}
                          onChange={(e) =>
                            setNewInjury((prev) => ({ ...prev, notes: e.target.value }))
                          }
                          rows={2}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="is_active"
                          checked={newInjury.is_active}
                          onCheckedChange={(checked) =>
                            setNewInjury((prev) => ({ ...prev, is_active: checked === true }))
                          }
                        />
                        <Label htmlFor="is_active" className="text-sm">
                          Currently active injury
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleAddInjury} disabled={!newInjury.area}>
                          Add Injury
                        </Button>
                        <Button variant="outline" onClick={() => setShowInjuryForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {(formData.injuries?.length || 0) === 0 && !showInjuryForm && (
                  <div className="flex flex-col items-center justify-center p-8 rounded-lg border border-dashed text-center">
                    <AlertTriangle className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No injuries or limitations recorded
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Click "Add Injury" if you have any current or past issues
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={currentStep === 0 ? onCancel : handlePrevious}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          {currentStep === 0 ? 'Cancel' : 'Previous'}
        </Button>

        <div className="flex gap-2">
          {!isLastStep ? (
            <Button onClick={handleNext} disabled={!isStepValid()}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={upsertProfile.isPending}
            >
              {upsertProfile.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Complete Profile
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
